import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import moment from 'moment';

import { FHButton, FHSignatureDialog, FProgressBar } from '../../components/base';
import { btSupervisionActions, providerAssignmentActions } from '../../store';
import useBtSupervisionEditable from '../../hooks/useBtSupervisionEditable';
import { history } from '../../helpers';
import { FORTA_BLACK } from '../../utils/colors';
import { getUtcOffset } from '../../utils/common';
import PageContainer from '../../components/layout/PageContainer';
import AdditionalComments from '../../components/layout/supervision-notes/bt/AdditionalComments';
import BehaviorManagement from '../../components/layout/supervision-notes/bt/BehaviorManagement';
import BtSupervisionInfo from '../../components/layout/supervision-notes/bt/BtSupervisionInfo';
import SkillAcquisition from '../../components/layout/supervision-notes/bt/SkillAcquisition';
import OtherBtSupervision from '../../components/layout/supervision-notes/bt/Other';
import { Themes } from '../../constants';
import { BT_SUPERVISION_INITIAL_STATE } from '../../components/layout/supervision-notes/bt/constants';
import { validateSupervision } from '../../components/layout/supervision-notes/bt/utils';

import type { IBtSupervision } from '../../utils/types';
import type { RootState } from '../../store';

const renderBCBASignatureMessage = () => (
  <Box>
    <Typography variant="body1" align="left" color={FORTA_BLACK} fontSize={12}>
      By signing and submitting these notes, plan and/or records (each a “record”), I hereby attest
      that (i) the entry for the date of service listed in the record accurately and completely
      reflects the services rendered by the named clinician herein; (ii) I have thoroughly reviewed
      the records; and (iii) I have discussed all applicable findings, outcomes, issues or
      discrepancies with the client and/or client&apos;s legal guardian, as appropriate.
    </Typography>
    <br />
    <Typography variant="body1" align="left" color={FORTA_BLACK} fontSize={12}>
      I understand that any falsification, omission, or concealment of facts may subject me to
      administrative, civil, or criminal liability as well as termination of my employment with the
      company.
    </Typography>
  </Box>
);

function BtSupervisionNote() {
  const { id: btSupervisionId } = useParams();
  const dispatch = useDispatch();
  const isEditable = useBtSupervisionEditable();
  const { getAccessTokenSilently } = useAuth0();

  const { current: btSupervision, loading: btSpLoading } = useSelector(
    (state: RootState) => state.btSupervision,
  );

  const [openSignature, setOpenSignature] = useState<boolean>(false);
  const [btSupervisionData, setBtSupervisionData] = useState<IBtSupervision>(
    BT_SUPERVISION_INITIAL_STATE,
  );

  useEffect(() => {
    return () => {
      dispatch(providerAssignmentActions.clearAssignment());
    };
  }, []);

  useEffect(() => {
    if (!btSupervisionId || btSupervisionId === 'new') {
      setBtSupervisionData(BT_SUPERVISION_INITIAL_STATE);
      return;
    }
    if (!btSupervision) {
      setBtSupervisionData(BT_SUPERVISION_INITIAL_STATE);
      return;
    }
    setBtSupervisionData(btSupervision);
  }, [btSupervision, btSupervisionId]);

  const handleSignatureDialogOpen = () => {
    if (!validateSupervision(btSupervisionData)) return;
    setOpenSignature(true);
  };

  const handleUpdate = async (key: string, value: string) => {
    if (!btSupervision || !key) return;
    const btSupervisionWithoutFields = (({
      client_id: _client_id,
      utc_offset_date_start: _utc_offset_date_start,
      bt_provider_id: _bt_provider_id,
      date_start: _date_start,
      duration: _duration,
      location: _location,
      signature: _signature,
      utc_offset_date_signed: _utc_offset_date_signed,
      date_signed: _date_signed,
      id: _id,
      ...rest
    }) => rest)(btSupervision);
    const stringifiedValue = String(value);

    const data = { ...btSupervisionWithoutFields, [key]: stringifiedValue };
    setBtSupervisionData((current) => ({ ...current, [key]: stringifiedValue }));

    if (!btSupervisionId || btSupervisionId === 'new') return;

    const response = await dispatch(
      btSupervisionActions.updateBtSupervisionData({
        btSupervisionId,
        data,
        getAccessTokenSilently,
      }),
    );
    if (!response || response.error) {
      setBtSupervisionData(btSupervision);
      return;
    }
  };

  const handleConclude = async (dataURL: string) => {
    if (!dataURL) return;
    const data = {
      signature: dataURL,
      date_signed: moment(new Date()),
      utc_offset_date_signed: getUtcOffset(moment(new Date())),
    };
    const response = await dispatch(
      btSupervisionActions.stopBtSupervision({
        btSupervisionId,
        data,
        getAccessTokenSilently,
      }),
    );
    setOpenSignature(false);
    if (!response || response.error) {
      return;
    }
    history.navigate('/dashboard');
  };

  return (
    <PageContainer sx={{ px: 3, pt: 3 }} dataTestId="btSupervisionNotePageContainer">
      {btSpLoading && <FProgressBar />}
      <BtSupervisionInfo />
      <SkillAcquisition
        isEditable={isEditable && !btSpLoading}
        btSupervision={btSupervisionData}
        onUpdateValue={handleUpdate}
      />
      <BehaviorManagement
        isEditable={isEditable && !btSpLoading}
        btSupervision={btSupervisionData}
        onUpdateValue={handleUpdate}
      />
      <OtherBtSupervision
        isEditable={isEditable && !btSpLoading}
        btSupervision={btSupervisionData}
        onUpdateValue={handleUpdate}
      />
      <AdditionalComments
        isEditable={isEditable && !btSpLoading}
        btSupervision={btSupervisionData}
        onUpdateValue={handleUpdate}
      />

      {isEditable && (
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
          <FHButton
            data-testid="concludebtSupervisionButton"
            sx={{ width: 200 }}
            disabled={!isEditable}
            onClick={handleSignatureDialogOpen}
          >
            Conclude
          </FHButton>
        </Box>
      )}
      <FHSignatureDialog
        headerText="Signature"
        cancelText="Clear Signature"
        open={openSignature}
        onClose={() => setOpenSignature(false)}
        onSign={(dataURL: string) => handleConclude(dataURL)}
        colorTheme={Themes.Primary}
        contentRenderer={renderBCBASignatureMessage}
      />
    </PageContainer>
  );
}

export default BtSupervisionNote;
